import React, { useEffect, useRef } from 'react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';

// Body content is intentionally art-directed (Fraunces/IBM Plex Mono, dark gold
// palette) rather than the site's standard Tailwind system — same treatment as
// a stand-alone thesis/investor piece, just bookended with the normal site chrome.
export default function ProofLayerPage() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const items = containerRef.current?.querySelectorAll<HTMLElement>('.pillar') ?? [];
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15 },
      );
      items.forEach((el) => io.observe(el));
      return () => io.disconnect();
    }
    items.forEach((el) => el.classList.add('is-visible'));
    return undefined;
  }, []);

  return (
    <div className="min-h-screen bg-bg">
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link
        href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700;9..144,900&family=IBM+Plex+Mono:wght@400;500&display=swap"
        rel="stylesheet"
      />
      <style>{`
        .proof-layer{
          --pl-bg: #131319;
          --pl-bg-panel: #1b1c23;
          --pl-gold: #c9a46c;
          --pl-gold-dim: #8a6f47;
          --pl-gold-bright: #e3c68f;
          --pl-ink: #6d7d92;
          --pl-text: #ece8de;
          --pl-text-muted: #9a9d9f;
          --pl-objection-bg: #c9a46c;
          --pl-objection-text: #1b1c23;
          --pl-line: rgba(201,164,108,0.18);
          --pl-serif: 'Fraunces', serif;
          --pl-mono: 'IBM Plex Mono', monospace;
          background: var(--pl-bg);
          color: var(--pl-text);
          font-family: 'Inter', sans-serif;
          line-height: 1.55;
          -webkit-font-smoothing: antialiased;
          position: relative;
        }
        .proof-layer ::selection{ background: var(--pl-gold-dim); color: var(--pl-text); }
        .proof-layer a{ color: inherit; }
        .proof-layer .wrap{ max-width: 1120px; margin: 0 auto; padding: 0 32px; }
        .proof-layer .grain{
          position: fixed; inset: 0; pointer-events: none; opacity: 0.05;
          background-image: radial-gradient(rgba(255,255,255,0.6) 0.6px, transparent 0.6px);
          background-size: 3px 3px; z-index: 0;
        }
        .proof-layer .hero{ position: relative; padding: 90px 0 100px; overflow: hidden; border-bottom: 1px solid var(--pl-line); }
        .proof-layer .hero-watermark{
          position: absolute; top: 50%; right: -6%; transform: translateY(-50%);
          width: 620px; height: 620px; opacity: 0.05; z-index: 0;
        }
        .proof-layer .hero-watermark svg{ width: 100%; height: 100%; }
        .proof-layer .eyebrow{
          font-family: var(--pl-mono); font-size: 12.5px; letter-spacing: 0.22em; text-transform: uppercase;
          color: var(--pl-gold); display: flex; align-items: center; gap: 14px; margin-bottom: 28px;
        }
        .proof-layer .eyebrow::before{ content: ""; width: 34px; height: 1px; background: var(--pl-gold-dim); }
        .proof-layer .hero h1{
          font-family: var(--pl-serif); font-optical-sizing: auto; font-weight: 600;
          font-size: clamp(40px, 6vw, 76px); line-height: 1.04; letter-spacing: -0.01em;
          margin: 0 0 28px; max-width: 820px; position: relative; z-index: 1;
        }
        .proof-layer .hero h1 em{ font-style: italic; font-weight: 400; color: var(--pl-gold); }
        .proof-layer .hero p.sub{ font-size: 19px; color: var(--pl-text-muted); max-width: 560px; margin: 0; position: relative; z-index: 1; }
        .proof-layer .hero-meta{
          margin-top: 56px; display: flex; gap: 48px; flex-wrap: wrap;
          font-family: var(--pl-mono); font-size: 12px; color: var(--pl-ink); position: relative; z-index: 1;
        }
        .proof-layer .hero-meta div span{
          display: block; color: var(--pl-text-muted); font-size: 11px; letter-spacing: 0.08em;
          text-transform: uppercase; margin-bottom: 6px;
        }
        .proof-layer .hero-meta div strong{ color: var(--pl-gold-bright); font-weight: 500; }
        .proof-layer .section-head{ padding: 72px 0 8px; display: flex; align-items: baseline; justify-content: space-between; gap: 24px; flex-wrap: wrap; }
        .proof-layer .section-head h2{ font-family: var(--pl-serif); font-weight: 600; font-size: clamp(26px, 3.4vw, 34px); margin: 0; letter-spacing: -0.005em; }
        .proof-layer .section-head .count{ font-family: var(--pl-mono); font-size: 12px; color: var(--pl-ink); letter-spacing: 0.1em; }
        .proof-layer .pillars{ padding: 24px 0 60px; display: grid; grid-template-columns: repeat(2, 1fr); gap: 1px; background: var(--pl-line); border: 1px solid var(--pl-line); }
        @media (max-width: 760px){ .proof-layer .pillars{ grid-template-columns: 1fr; } }
        .proof-layer .pillar{
          background: var(--pl-bg-panel); padding: 40px 40px 36px; position: relative;
          opacity: 0; transform: translateY(14px); transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .proof-layer .pillar.is-visible{ opacity: 1; transform: translateY(0); }
        .proof-layer .pillar.wide{ grid-column: 1 / -1; }
        .proof-layer .stamp{
          width: 52px; height: 52px; border-radius: 50%; border: 1.5px solid var(--pl-gold-dim);
          display: flex; align-items: center; justify-content: center; font-family: var(--pl-mono);
          font-size: 15px; color: var(--pl-gold-bright); letter-spacing: 0.02em; position: relative;
          margin-bottom: 22px; flex-shrink: 0;
        }
        .proof-layer .stamp::before{ content: ""; position: absolute; inset: 5px; border-radius: 50%; border: 1px solid var(--pl-gold-dim); opacity: 0.5; }
        .proof-layer .pillar h3{ font-family: var(--pl-serif); font-weight: 600; font-size: 22px; margin: 0 0 6px; letter-spacing: -0.005em; }
        .proof-layer .pillar .kicker{ font-family: var(--pl-mono); font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--pl-gold); margin: 0 0 16px; }
        .proof-layer .pillar p{ margin: 0; color: var(--pl-text-muted); font-size: 15.5px; max-width: 46ch; }
        .proof-layer .pillar strong{ color: var(--pl-text); font-weight: 600; }
        .proof-layer .pillar.objection{ background: var(--pl-objection-bg); color: var(--pl-objection-text); }
        .proof-layer .pillar.objection .stamp{ border-color: var(--pl-objection-text); color: var(--pl-objection-text); }
        .proof-layer .pillar.objection .stamp::before{ border-color: var(--pl-objection-text); }
        .proof-layer .pillar.objection .kicker{ color: var(--pl-objection-text); opacity: 0.7; }
        .proof-layer .pillar.objection h3{ color: var(--pl-objection-text); }
        .proof-layer .pillar.objection p{ color: var(--pl-objection-text); opacity: 0.85; }
        .proof-layer .pillar.objection strong{ color: var(--pl-objection-text); }
        .proof-layer .pillar.objection .flag{
          position: absolute; top: 40px; right: 40px; font-family: var(--pl-mono);
          font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; opacity: 0.55;
        }
        .proof-layer .closing{ border-top: 1px solid var(--pl-line); padding: 72px 0 90px; display: flex; justify-content: space-between; align-items: flex-end; gap: 32px; flex-wrap: wrap; }
        .proof-layer .closing h2{ font-family: var(--pl-serif); font-weight: 600; font-style: italic; font-size: clamp(24px, 3vw, 32px); max-width: 520px; margin: 0; color: var(--pl-gold-bright); line-height: 1.3; }
        .proof-layer .closing .foot-mark{ font-family: var(--pl-mono); font-size: 11.5px; color: var(--pl-ink); text-align: right; letter-spacing: 0.06em; line-height: 1.9; }
        @media (prefers-reduced-motion: reduce){ .proof-layer .pillar{ transition: none; opacity: 1; transform: none; } }
        @media (max-width: 600px){
          .proof-layer .hero{ padding: 60px 0 70px; }
          .proof-layer .wrap{ padding: 0 20px; }
          .proof-layer .pillar{ padding: 32px 24px 30px; }
          .proof-layer .closing{ flex-direction: column; align-items: flex-start; }
          .proof-layer .closing .foot-mark{ text-align: left; }
          .proof-layer .pillar.objection .flag{ position: static; display: block; margin-bottom: 12px; }
        }
      `}</style>

      <SiteNav />

      <div className="proof-layer" ref={containerRef}>
        <div className="grain" />

        <section className="hero">
          <div className="hero-watermark" aria-hidden="true">
            <svg viewBox="0 0 200 200">
              <circle cx="100" cy="100" r="98" fill="none" stroke="#c9a46c" strokeWidth="0.6" />
              <circle cx="100" cy="100" r="86" fill="none" stroke="#c9a46c" strokeWidth="0.6" />
              <circle cx="100" cy="100" r="74" fill="none" stroke="#c9a46c" strokeWidth="0.6" />
              <text x="100" y="112" textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="26" fill="#c9a46c">999.9</text>
            </svg>
          </div>
          <div className="wrap">
            <div className="eyebrow">The Proof Layer</div>
            <h1>Underneath the ledger,<br />something <em>nobody prints.</em></h1>
            <p className="sub">Seven marks of proof for why bullion — not a fiat balance, not a synthetic peg — sits at the base of the reserve.</p>
            <div className="hero-meta">
              <div><span>Reserve asset</span><strong>Physical gold, non-custodial</strong></div>
              <div><span>Pricing model</span><strong>Set at redemption, not at trade</strong></div>
              <div><span>Function</span><strong>Proof of reserves, not return engine</strong></div>
            </div>
          </div>
        </section>

        <div className="wrap">
          <div className="section-head">
            <h2>Seven pillars</h2>
            <div className="count">I — VII</div>
          </div>
        </div>

        <div className="wrap">
          <div className="pillars">
            <div className="pillar">
              <div className="stamp">I</div>
              <div className="kicker">Monetary history</div>
              <h3>Timeless store of value</h3>
              <p>No issuer, no maturity date, no counterparty who can default. Gold has held purchasing power across every currency regime that has tried to replace it — the claim isn't that it never loses value, it's that <strong>nothing behind the ledger can be defaulted on.</strong></p>
            </div>

            <div className="pillar">
              <div className="stamp">II</div>
              <div className="kicker">Platform mechanics</div>
              <h3>Constructed yield</h3>
              <p>Gold pays no coupon — the platform does. <strong>Contribution timing, vesting shape, and refund recycling</strong> generate yield-like behavior on top of a stable denominator, rather than asking the metal itself to produce a return.</p>
            </div>

            <div className="pillar objection wide">
              <div className="flag">Addressed directly, not softened</div>
              <div className="stamp">III</div>
              <div className="kicker">The honest objection</div>
              <h3>Non-yield-bearing — and that's the point</h3>
              <p>Gold carries negative cost: storage, insurance, opportunity cost against equities. This is real and shouldn't be argued away. But the reserve was never asked to out-earn a portfolio — it was asked to hold a claim that <strong>no one can quietly rehypothecate, print, or default on.</strong> Non-yield-bearing is a weakness for a return engine. It's a feature for a proof layer.</p>
            </div>

            <div className="pillar">
              <div className="stamp">IV</div>
              <div className="kicker">Jurisdiction</div>
              <h3>Apolitical collateral</h3>
              <p>No single central bank, no single currency's policy decisions. For income earned across borders, a <strong>jurisdiction-neutral reserve</strong> outranks one tied to any one government's balance sheet.</p>
            </div>

            <div className="pillar">
              <div className="stamp">V</div>
              <div className="kicker">Portfolio behavior</div>
              <h3>Correlation diversifier</h3>
              <p>Not an inflation hedge on demand — a <strong>low, unstable correlation</strong> to equities and bonds. Most valuable precisely when income is procyclical and irregular, which is the default condition of gig earnings.</p>
            </div>

            <div className="pillar">
              <div className="stamp">VI</div>
              <div className="kicker">Saver psychology</div>
              <h3>Behavioral anchor</h3>
              <p>A gram is a gram in every currency. Savings tied to a <strong>physical referent</strong> may hold steadier through panic than a number denominated only inside an app.</p>
            </div>

            <div className="pillar">
              <div className="stamp">VII</div>
              <div className="kicker">Regulatory design</div>
              <h3>Regulatory boundary</h3>
              <p>Price set at redemption, not at trade. A recognized, audited commodity gives regulators a bright line: this is <strong>a reserve redemption, not a security changing hands.</strong></p>
            </div>
          </div>
        </div>

        <div className="wrap">
          <div className="closing">
            <h2>"Proof, not promise — a reserve you can weigh, not just trust."</h2>
            <div className="foot-mark">
              GOLD AS PROOF LAYER<br />
              NON-CUSTODIAL ARCHITECTURE<br />
              FIN. 999.9
            </div>
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}
